import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trash2, RotateCcw, Loader2, Film, Tv, Layers, Clock, AlertCircle } from 'lucide-react';
import {
  useDeletedWatchlistsQuery,
  useRestoreWatchlistMutation,
  type WatchlistMeta,
} from '../hooks/useListsQuery';
import { useAuth } from '../contexts/AuthContext';
import { useBackNavigation } from '../hooks/useBackNavigation';

function formatRelativeTime(dateString: string | undefined): string {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString();
}

interface DeletedListCardProps {
  list: WatchlistMeta & { deleted_at?: string };
  onRestore: () => void;
  isRestoring: boolean;
}

function DeletedListCard({ list, onRestore, isRestoring }: DeletedListCardProps) {
  const MediaIcon = list.media_filter === 'movie' ? Film : list.media_filter === 'tv' ? Tv : Layers;

  return (
    <div className="bg-[#0F1419] ring-1 ring-white/[0.06] rounded-xl p-4 flex items-center gap-4">
      {/* Cover/Icon */}
      <div className="w-16 h-16 rounded-lg bg-[#1A1F26] flex-shrink-0 overflow-hidden">
        {list.cover_url ? (
          <img
            src={list.cover_url}
            alt={list.name}
            className="w-full h-full object-cover opacity-50"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MediaIcon size={24} className="text-slate-600" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-white truncate">{list.name}</h3>
        <p className="text-sm text-slate-500 truncate">
          {list.item_count} {list.item_count === 1 ? 'item' : 'items'}
        </p>
        {list.deleted_at && (
          <p className="text-xs text-slate-600 flex items-center gap-1 mt-1">
            <Clock size={12} />
            Deleted {formatRelativeTime(list.deleted_at)}
          </p>
        )}
      </div>

      {/* Restore Button */}
      <button
        onClick={onRestore}
        disabled={isRestoring}
        className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-400 transition-colors disabled:opacity-50"
      >
        {isRestoring ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Restoring...
          </>
        ) : (
          <>
            <RotateCcw size={16} />
            Restore
          </>
        )}
      </button>
    </div>
  );
}

function EmptyState({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-16 h-16 rounded-full bg-[#1A1F26] flex items-center justify-center mb-4">
        <Trash2 size={32} className="text-slate-600" />
      </div>
      <h2 className="text-xl font-semibold text-white mb-2">No Deleted Lists</h2>
      <p className="text-slate-500 text-center max-w-md">
        When you delete a list, it will appear here for recovery. Deleted lists are kept for 30 days before being permanently removed.
      </p>
      <button
        onClick={onBack}
        className="mt-6 px-4 py-2 bg-[#1A1F26] ring-1 ring-white/[0.08] text-slate-300 rounded-lg hover:bg-slate-700 transition-colors inline-flex items-center gap-2"
      >
        <ArrowLeft size={16} />
        Back
      </button>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="bg-[#0F1419] ring-1 ring-white/[0.06] rounded-xl p-4 flex items-center gap-4 animate-pulse"
        >
          <div className="w-16 h-16 rounded-lg bg-[#1A1F26]" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-[#1A1F26] rounded w-32" />
            <div className="h-3 bg-[#1A1F26] rounded w-20" />
          </div>
          <div className="w-24 h-9 bg-[#1A1F26] rounded-lg" />
        </div>
      ))}
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
        <AlertCircle size={32} className="text-red-400" />
      </div>
      <h2 className="text-xl font-semibold text-white mb-2">Something Went Wrong</h2>
      <p className="text-slate-500 text-center max-w-md mb-6">{error}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-400 transition-colors"
      >
        Try Again
      </button>
    </div>
  );
}

function UnauthenticatedState({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-16 h-16 rounded-full bg-[#1A1F26] flex items-center justify-center mb-4">
        <Trash2 size={32} className="text-slate-600" />
      </div>
      <h2 className="text-xl font-semibold text-white mb-2">Sign In Required</h2>
      <p className="text-slate-500 text-center max-w-md">
        You need to be signed in to view your deleted lists.
      </p>
      <Link
        to="/login"
        className="mt-6 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-400 transition-colors"
      >
        Sign In
      </Link>
      <button
        onClick={onBack}
        className="mt-3 inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
      >
        <ArrowLeft size={16} />
        <span className="text-sm font-medium">Back</span>
      </button>
    </div>
  );
}

export function DeletedListsPage() {
  const { user } = useAuth();
  const goBack = useBackNavigation();
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const { watchlists, isLoading, error, refetch } = useDeletedWatchlistsQuery();
  const restoreMutation = useRestoreWatchlistMutation();

  const handleRestore = async (listId: string) => {
    setRestoringId(listId);
    try {
      await restoreMutation.mutateAsync({ listId });
    } finally {
      setRestoringId(null);
    }
  };

  // Unauthenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <UnauthenticatedState onBack={goBack} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={goBack}
            className="p-2 text-slate-400 hover:text-white hover:bg-[#1A1F26] rounded-lg transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Deleted Lists</h1>
            <p className="text-sm text-slate-500">
              Lists are kept for 30 days before permanent deletion
            </p>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error.message} onRetry={() => refetch()} />
        ) : watchlists.length === 0 ? (
          <EmptyState onBack={goBack} />
        ) : (
          <div className="space-y-4">
            {watchlists.map((list) => (
              <DeletedListCard
                key={list.id}
                list={list as WatchlistMeta & { deleted_at?: string }}
                onRestore={() => handleRestore(list.id)}
                isRestoring={restoringId === list.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DeletedListsPage;
